import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Phone, Zap, AlertTriangle, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const URGENCY = [
  { id: "emergency", label: "Emergency", Icon: AlertTriangle, color: "border-red-500 text-red-400 data-[on=true]:bg-red-500/20" },
  { id: "urgent", label: "Urgent", Icon: Zap, color: "border-amber-500 text-amber-400 data-[on=true]:bg-amber-500/20" },
  { id: "standard", label: "Standard", Icon: Clock, color: "border-slate-600 text-slate-300 data-[on=true]:bg-slate-500/20" },
];

export const DispatchModal = ({ provider, open, onClose }) => {
  const [form, setForm] = useState({ customer_name: "", phone: "", vehicle_reg: "", vehicle_model: "", location: "", urgency: "urgent", notes: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/providers/${provider.id}/dispatch`, form);
      setSent(true);
      toast.success("Call-out request sent!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setSent(false); setForm({ customer_name: "", phone: "", vehicle_reg: "", vehicle_model: "", location: "", urgency: "urgent", notes: "" }); onClose(); };

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && reset()}>
      <DialogContent data-testid="dispatch-modal" className="bg-slate-900 border-slate-800 text-slate-100 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl">
            {sent ? "Request Sent" : `Request Call-out`}
          </DialogTitle>
          <p className="text-sm text-slate-400">{provider.business_name} · {provider.city}</p>
        </DialogHeader>

        {sent ? (
          <div className="py-6 text-center space-y-4">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/20 flex items-center justify-center pulse-ring">
              <Zap className="text-emerald-400" size={30} />
            </div>
            <p className="text-slate-300">Your request has reached <b>{provider.business_name}</b>. They'll call you back shortly. For emergencies, call them directly now.</p>
            <a href={`tel:${provider.phone}`} className="block">
              <Button className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-xl h-11">
                <Phone size={16} className="mr-2" /> Call {provider.phone}
              </Button>
            </a>
            <Button onClick={reset} variant="ghost" className="text-slate-400">Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-slate-300 text-xs">Urgency</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {URGENCY.map((u) => (
                  <button type="button" key={u.id} data-on={form.urgency === u.id}
                    onClick={() => set("urgency", u.id)}
                    className={`flex flex-col items-center gap-1 py-2 rounded-xl border bg-slate-800/40 text-xs font-semibold transition-all ${u.color}`}>
                    <u.Icon size={16} /> {u.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-slate-300 text-xs">Your name</Label>
                <Input data-testid="dispatch-form-name-input" required value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)}
                  className="bg-slate-800 border-slate-700 mt-1" placeholder="John Smith" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Phone</Label>
                <Input data-testid="dispatch-form-phone-input" required value={form.phone} onChange={(e) => set("phone", e.target.value)}
                  className="bg-slate-800 border-slate-700 mt-1" placeholder="07..." />
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Your location</Label>
              <Input data-testid="dispatch-form-location-input" required value={form.location} onChange={(e) => set("location", e.target.value)}
                className="bg-slate-800 border-slate-700 mt-1" placeholder="Postcode or address / junction" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-slate-300 text-xs">Vehicle reg</Label>
                <Input data-testid="dispatch-form-reg-input" value={form.vehicle_reg} onChange={(e) => set("vehicle_reg", e.target.value)}
                  className="bg-slate-800 border-slate-700 mt-1 uppercase" placeholder="AB12 CDE" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Make / model</Label>
                <Input value={form.vehicle_model} onChange={(e) => set("vehicle_model", e.target.value)}
                  className="bg-slate-800 border-slate-700 mt-1" placeholder="Ford Focus" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-xs">What's the issue?</Label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
                className="bg-slate-800 border-slate-700 mt-1" rows={2} placeholder="Describe the problem..." />
            </div>
            <Button data-testid="dispatch-form-submit-button" type="submit" disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-xl h-11">
              {loading ? "Sending..." : <><Zap size={16} className="mr-2" /> Send Call-out Request</>}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
